import { Linkedin } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const members = [
  {
    name: "Samara Gualberto",
    role: "CEO",
    credential: "Advogada Marítima · Sócia na Advocacia RMM",
    bio: "Mais de 12 anos em Direito Marítimo. Ex-presidente da Comissão de Direito Marítimo da OAB/PA.",
    linkedin: "https://www.linkedin.com/in/samara-gualberto-21175915a/",
    photo: "/samara-perfil-2.jpeg",
  },
  {
    name: "Luiz Scotta",
    role: "Diretor de Tecnologia",
    credential: "Engenheiro de Software · Especialista em Automação e IA",
    bio: "Ciências da Computação, pós-graduado em Engenharia de Dados e IA.",
    linkedin: "https://www.linkedin.com/in/luiz-scotta-450572213/",
    photo: "/luiz-perfil.png",
  },
  {
    name: "Kledison Gomes",
    role: "Gerente de Processos",
    credential: "Engenheiro Mecânico · Administração · +8 anos com AFRMM",
    bio: "Mais de 8 anos trabalhando diretamente com AFRMM e fluxos regulatórios.",
    linkedin: "https://www.linkedin.com/in/kledison-gomes-0a83a0178/",
    photo: "/kledison-perfil.jpg",
  },
];

const Team = () => {
  const sectionRef = useScrollReveal();

  return (
    <section
      id="equipe"
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="py-24 relative overflow-hidden bg-grid"
      style={{ background: "var(--color-preto-marca)" }}
    >
      <div className="container mx-auto px-4 lg:px-6 relative">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16" data-animate>
          <div
            className="eyebrow justify-center"
            style={{ color: "var(--color-cinza-ancora)" }}
          >
            Equipe
          </div>
          <h2
            className="heading-h2 mb-4"
            style={{ color: "var(--color-branco-vela)" }}
          >
            Pessoas que vieram para transformar o mercado.
          </h2>
          <div
            className="w-14 h-px mx-auto"
            style={{ background: "var(--color-cinza-ancora)", opacity: 0.3 }}
          />
        </div>

        {/* Team Cards */}
        <div className="grid md:grid-cols-3 gap-5 max-w-4xl mx-auto mb-16">
          {members.map((member, index) => (
            <div
              key={index}
              data-animate
              data-animate-delay={String(index + 1)}
              className="text-center p-7 rounded transition-all duration-200 cursor-default"
              style={{
                background: "var(--color-carvao)",
                border: "1px solid rgba(176, 175, 172, 0.12)",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.borderColor = "var(--color-dourado)";
                el.style.transform = "scale(1.02)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.borderColor = "rgba(176, 175, 172, 0.12)";
                el.style.transform = "scale(1)";
              }}
            >
              {/* Photo — quadrado, não circular */}
              <div
                className="w-20 h-20 mx-auto mb-5 overflow-hidden"
                style={{
                  borderRadius: "4px",
                  border: "1px solid rgba(176, 175, 172, 0.15)",
                }}
              >
                {member.photo ? (
                  <img
                    src={member.photo}
                    alt={`Foto de ${member.name}`}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ background: "rgba(242, 240, 236, 0.04)" }}
                  >
                    <span
                      className="text-2xl font-bold"
                      style={{ color: "rgba(176, 175, 172, 0.4)" }}
                    >
                      {member.name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>

              <h3
                className="font-bold text-lg tracking-tight"
                style={{ color: "var(--color-branco-vela)" }}
              >
                {member.name}
              </h3>
              <p
                className="text-xs font-medium uppercase tracking-wider mt-1"
                style={{ color: "var(--color-cinza-ancora)" }}
              >
                {member.role}
              </p>
              <p
                className="text-[11px] mt-1 mb-4 font-light"
                style={{ color: "var(--color-cinza-nevoa)" }}
              >
                {member.credential}
              </p>
              <p
                className="text-[13px] leading-relaxed mb-5 font-light"
                style={{ color: "var(--color-cinza-ancora)", lineHeight: 1.65 }}
              >
                {member.bio}
              </p>

              <a
                href={member.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center transition-colors duration-200 cursor-pointer"
                style={{ color: "var(--color-cinza-ancora)" }}
                aria-label={`LinkedIn de ${member.name}`}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLElement).style.color = "var(--color-dourado)")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLElement).style.color = "var(--color-cinza-ancora)")
                }
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          ))}
        </div>

        {/* Quote */}
        <div className="max-w-2xl mx-auto" data-animate>
          <blockquote
            className="text-base font-light leading-relaxed italic"
            style={{
              color: "var(--color-cinza-ancora)",
              borderLeft: "2px solid var(--color-dourado)",
              paddingLeft: "1.5rem",
            }}
          >
            "A SIATECH nasceu de uma oportunidade de mercado: empresas que não conhecem o AFRMM e 
            estão deixando o dinheiro na mesa, ou aquelas que fazer da forma antiga, lenta e com erros"
          </blockquote>
        </div>
      </div>
    </section>
  );
};

export default Team;
